import { useEffect, useMemo, useState } from 'react';
import { Copy, FileJson, GitBranch, ScrollText } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { OrchestrationHierarchyTree } from '@/components/learningTutorial/OrchestrationHierarchyTree';
import { OrchestrationPassLog } from '@/components/learningTutorial/OrchestrationPassLog';
import { orchestrationPassStatuses } from '@/lib/learningTutorial/orchestrationViewerTabs';

function formatJson(value) {
  if (value == null) return 'null';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

const TREE_SUPPORTED = new Set([
  'pass1', 'pass2', 'pass3', 'orchestration', 'derived_tree', 'dfs', 'script',
]);

function passStatusClass(state) {
  if (state === 'complete') return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
  if (state === 'fallback') return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
  return 'bg-slate-800 text-slate-500 border-slate-700';
}

export function OrchestrationJsonDialog({
  open,
  onOpenChange,
  title = 'Orchestration output',
  description,
  tabs = [],
  orchestration = null,
  initialTabId = null,
}) {
  const safeTabs = useMemo(
    () => (tabs || []).filter((t) => t && t.id && t.label),
    [tabs],
  );
  const [activeId, setActiveId] = useState(safeTabs[0]?.id || '');
  const [viewMode, setViewMode] = useState('tree');

  useEffect(() => {
    if (!safeTabs.length) return;
    const stillValid = safeTabs.some((t) => t.id === activeId);
    if (!stillValid) {
      setActiveId(safeTabs[0].id);
    }
  }, [safeTabs, activeId]);

  useEffect(() => {
    if (!open || !initialTabId) return;
    if (safeTabs.some((t) => t.id === initialTabId)) {
      setActiveId(initialTabId);
    }
  }, [open, initialTabId, safeTabs]);

  const activeTab = safeTabs.find((t) => t.id === activeId) || safeTabs[0];
  const isPassLogTab = activeTab?.id === 'pass_log';
  const body = activeTab?.data;
  const supportsTree = TREE_SUPPORTED.has(activeTab?.id);
  const passStatuses = useMemo(
    () => orchestrationPassStatuses(orchestration),
    [orchestration],
  );

  useEffect(() => {
    if (!supportsTree && !isPassLogTab && viewMode === 'tree') {
      setViewMode('json');
    }
  }, [supportsTree, isPassLogTab, viewMode]);

  const jsonPayload = useMemo(() => {
    if (isPassLogTab) return orchestration?.pass_log || [];
    return body;
  }, [isPassLogTab, orchestration, body]);

  const copyActive = async () => {
    try {
      await navigator.clipboard.writeText(formatJson(jsonPayload));
      toast.success('Copied JSON');
    } catch {
      toast.error('Could not copy');
    }
  };

  const handleViewPassOutput = (snapshotKey) => {
    if (safeTabs.some((t) => t.id === snapshotKey)) {
      setActiveId(snapshotKey);
      setViewMode('tree');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileJson className="w-5 h-5 text-violet-400" />
            {title}
          </DialogTitle>
          {description ? (
            <DialogDescription className="text-slate-400">{description}</DialogDescription>
          ) : null}
        </DialogHeader>

        {passStatuses.length > 0 ? (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-slate-500 mr-1">Passes:</span>
            {passStatuses.map((p) => (
              <span
                key={p.id}
                className={cn(
                  'text-xs px-2 py-1 rounded-md border',
                  passStatusClass(p.state),
                )}
              >
                {p.label}
                {p.state ? ` · ${p.state}` : ' · pending'}
              </span>
            ))}
          </div>
        ) : null}

        {safeTabs.length > 0 ? (
          <div className="flex flex-wrap gap-2 border-b border-slate-700 pb-2">
            {safeTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveId(tab.id)}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm transition-colors',
                  activeTab?.id === tab.id
                    ? 'bg-violet-600/30 text-violet-200 border border-violet-500/40'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800',
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-2">
          {supportsTree || isPassLogTab ? (
            <div className="inline-flex rounded-lg border border-slate-700 p-0.5 bg-slate-950">
              <button
                type="button"
                onClick={() => setViewMode('tree')}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors',
                  viewMode === 'tree'
                    ? 'bg-violet-600/40 text-violet-100'
                    : 'text-slate-400 hover:text-slate-200',
                )}
              >
                {isPassLogTab ? (
                  <ScrollText className="w-3.5 h-3.5" />
                ) : (
                  <GitBranch className="w-3.5 h-3.5" />
                )}
                {isPassLogTab ? 'Log' : 'Tree view'}
              </button>
              <button
                type="button"
                onClick={() => setViewMode('json')}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors',
                  viewMode === 'json'
                    ? 'bg-violet-600/40 text-violet-100'
                    : 'text-slate-400 hover:text-slate-200',
                )}
              >
                <FileJson className="w-3.5 h-3.5" />
                JSON
              </button>
            </div>
          ) : (
            <span className="text-xs text-slate-500">JSON only for this tab</span>
          )}
          {viewMode === 'json' ? (
            <Button type="button" variant="outline" size="sm" className="border-slate-600" onClick={copyActive}>
              <Copy className="w-4 h-4 mr-2" />
              Copy JSON
            </Button>
          ) : null}
        </div>

        <div className="flex-1 overflow-auto rounded-lg bg-slate-950 border border-slate-800 p-4 min-h-[280px]">
          {isPassLogTab && viewMode === 'tree' ? (
            <OrchestrationPassLog
              entries={orchestration?.pass_log || []}
              onViewOutput={handleViewPassOutput}
            />
          ) : viewMode === 'tree' && supportsTree ? (
            <OrchestrationHierarchyTree tabId={activeTab.id} data={body} />
          ) : (
            <pre className="text-xs leading-relaxed text-emerald-100/90 font-mono whitespace-pre-wrap">
              {formatJson(jsonPayload)}
            </pre>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
